'use client';

import { useQuery } from '@tanstack/react-query';
import { Serializer } from '@velocity-exchange/common';
import { PublicKey } from '@velocity-exchange/sdk';
import DataApiClient from 'src/utils/DataApiClient';
import { OrderActionRecord } from 'src/@types/orders';

interface ShortTermResponse {
	success: boolean;
	records: unknown[];
	meta: { nextPage: string | null };
}

function deserializeOrderActionRecords(
	records: unknown[]
): OrderActionRecord[] {
	return records.map((record) =>
		Serializer.Deserialize.UIOrderActionRecordV2(record as never)
	);
}

async function fetchAllOrderActionPages(
	userAccount: string,
	orderId: number
): Promise<OrderActionRecord[]> {
	const allRecords: OrderActionRecord[] = [];
	let nextToken: string | undefined = undefined;
	let keepFetching = true;
	while (keepFetching) {
		let res: ShortTermResponse | null = null;
		try {
			res = (await DataApiClient.fetchShortTermData<unknown>(
				'orderActions',
				nextToken,
				{ userAccount, orderId }
			)) as unknown as ShortTermResponse;
		} catch {
			break;
		}
		if (!res || res.success === false) {
			break;
		}
		try {
			const pageRecords = deserializeOrderActionRecords(res.records);
			allRecords.push(...pageRecords);
		} catch {
			// ignore page-level deserialization errors; keep partial data
		}
		if (res.meta.nextPage) {
			nextToken = res.meta.nextPage;
			continue;
		}
		keepFetching = false;
	}
	return allRecords;
}

interface Params {
	orderId: number;
	enabled: boolean;
	currentAccountPubKey?: PublicKey;
}

export default function useOrderActionHistory({
	orderId,
	currentAccountPubKey,
	enabled,
}: Params) {
	const userAccount = currentAccountPubKey?.toString();

	const queryResult = useQuery({
		queryKey: ['order-action-history', userAccount, orderId],
		queryFn: () => fetchAllOrderActionPages(userAccount as string, orderId),
		enabled: !!userAccount && enabled,
		staleTime: Infinity,
		gcTime: Infinity,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		refetchOnMount: false,
	});

	return {
		orderActionRecords: queryResult.data ?? [],
		...queryResult,
	};
}
